// Generated by Claude Code
import {
  createSearchSelect,
  type SearchSelectBaseProps,
} from '#components/composites/create-search-select.tsx'
import GuestIdentitiesApi from '#core/api/auth/GuestIdentitiesApi.ts'
import UsersApi from '#core/api/auth/UsersApi.ts'
import UserBasic from '#core/entities/auth/users/UserBasic.ts'
import { useCallback } from 'react'

export interface UserSearchSelectProps extends SearchSelectBaseProps<UserBasic> {
  selectedUsers?: UserBasic[]
  onSelectedUsersChange?: (selectedUsers: UserBasic[]) => void
  /**
   * When true, the option list also includes account-less guest submitters
   * (public/QR work-request requesters), not just company members. Off by default;
   * only the work-requests "Requested by" filter opts in.
   */
  includeGuests?: boolean
  /** User IDs to drop from the options (e.g. exclude the caller from an on-behalf picker). */
  excludeIds?: string[]
}

/**
 * Memoized fetcher for the picker's options. `listUsers` applies the search term
 * server-side (bounded page). When `includeGuests` is set, the guest submitters come
 * from their own endpoint ({@link GuestIdentitiesApi}) — a separate query, run in
 * parallel — and are adapted into `UserBasic` so the two merge into one uniform option
 * list. Users and guests are deliberately never fetched from one endpoint.
 */
export function useUserLoadOptions({ includeGuests, excludeIds }: Pick<UserSearchSelectProps, 'includeGuests' | 'excludeIds'>) {
  return useCallback(async (query: string) => {
    const search = query.trim() || undefined
    const excluded = new Set(excludeIds ?? [])
    const keep = (users: UserBasic[]) => (excluded.size ? users.filter((user) => !excluded.has(user.id)) : users)
    if (!includeGuests) return keep(await new UsersApi().listUsers(search))
    const [users, guests] = await Promise.all([
      new UsersApi().listUsers(search),
      new GuestIdentitiesApi().listGuests(search),
    ])
    return keep([...users, ...guests.map((g) => UserBasic.fromGuest(g))])
  }, [includeGuests, excludeIds])
}

/**
 * Server-side user picker. Thin domain wrapper over `ApiSearchSelect`.
 * Use this single-type picker wherever only a person can be chosen (e.g. the work-requests
 * "Requested by" filter, `includeGuests`), as opposed to the combined user+team `AssigneeSearchSelect`.
 */
export const UserSearchSelect = createSearchSelect<UserBasic, UserSearchSelectProps>({
  toItem: (user) => ({
    value: user.id,
    label: user.displayName,
  }),
  selectedKey: 'selectedUsers',
  onSelectedChangeKey: 'onSelectedUsersChange',
  useLoadOptions: useUserLoadOptions,
  ownProps: ['includeGuests', 'excludeIds'],
})

export default UserSearchSelect
